'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Button, Badge, sectionTitleVariants } from '@/components/ui/design-system';
import { ArrowRight, ShieldCheck, Package, Building2, UtensilsCrossed, Wine, Home, Store } from 'lucide-react';
import { motion, type Variants } from 'framer-motion';
import { getIndustriesWithApplications, type Industry, IndustryApplication } from '@/lib/data/industries';
import { cn } from '@/lib/utils';

// Import CSS for dark mode styling
// Removed legacy industries.css import after full migration to Tailwind/DS utilities.

// Import the extracted components
import IndustryHero from './IndustryHero';
import IndustryGrid from './IndustryGrid';
import IndustriesStats from './IndustriesStats';
import IndustriesTestimonials from './IndustriesTestimonials';
import IndustriesCTA from './IndustriesCTA';
import IndustriesContactCTA from './IndustriesContactCTA';

// Icon map for industries
const industryIconMap: { [key: string]: React.ReactElement } = {
  'wholesale-distribution': <Building2 className="h-4 w-4" />,
  'b2b-business': <ShieldCheck className="h-4 w-4" />,
  'food-packaging': <UtensilsCrossed className="h-4 w-4" />,
  'beverages-packaging': <Wine className="h-4 w-4" />,
  'household-packaging': <Home className="h-4 w-4" />,
  'startups-retailers': <Store className="h-4 w-4" />,
  'default': <Package className="h-4 w-4" />,
};

export default function IndustriesContent() {
  const searchParams = useSearchParams();
  const [industries, setIndustries] = useState<Industry[]>([]);
  const gridRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const initialScrollDoneRef = useRef(false);
  // Default to 'all' industry
  const [activeIndustry, setActiveIndustry] = useState('all');
  const [isFiltering, setIsFiltering] = useState(false);
  const [gridItems, setGridItems] = useState<(Industry | IndustryApplication)[]>([]);
  const [displayMode, setDisplayMode] = useState<'industries' | 'applications'>('industries');
  const [showAllApplications, setShowAllApplications] = useState(false);
  const [allApplications, setAllApplications] = useState<IndustryApplication[]>([]);
  const initialLoadRef = useRef(true);

  // Constants for application display
  const INITIAL_APPLICATIONS_COUNT = 6;

  // Dynamically generate industries list from fetched data
  const industriesList = React.useMemo(() => {
    const list = industries.map(industry => ({
      id: industry.slug,
      name: industry.name,
      icon: industryIconMap[industry.slug] || industryIconMap.default,
    }));
    return [
      { id: 'all', name: 'All Industries', icon: <Package className="h-4 w-4" /> },
      ...list,
    ];
  }, [industries]);

  // Smooth scroll function with easing
  const smoothScrollTo = (element: HTMLElement, offset: number) => {
    const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - offset;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 600; // milliseconds
    let start: number | null = null;

    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const percentage = Math.min(progress / duration, 1);
      
      // Easing function (easeInOutCubic)
      const easeInOutCubic = (t: number) => 
        t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
      
      window.scrollTo(0, startPosition + distance * easeInOutCubic(percentage));
      
      if (progress < duration) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  };

  // Handle industry change with smooth transition
  const handleIndustryChange = (industryId: string) => {
    // Allow re-activating the same industry if we're still in industries mode
    if (industryId === activeIndustry && displayMode === 'applications') return;

    setActiveIndustry(industryId);
    setIsFiltering(true);
    initialLoadRef.current = false;

    if (industryId === 'all') {
      setGridItems(industries);
      setDisplayMode('industries');
      setShowAllApplications(false);
    } else {
      const selectedIndustry = industries.find(ind => ind.slug === industryId);
      if (selectedIndustry && selectedIndustry.applications) {
        setAllApplications(selectedIndustry.applications);
        const initialApps = selectedIndustry.applications.slice(0, INITIAL_APPLICATIONS_COUNT);
        setGridItems(initialApps);
        setDisplayMode('applications');
        setShowAllApplications(false);
      } else {
        setGridItems([]); // Or handle as an error/empty state
        setDisplayMode('applications');
        setAllApplications([]);
        setShowAllApplications(false);
      }
    }

    if (gridRef.current) {
      smoothScrollTo(gridRef.current, 80);
    }

    // Reset filtering state after animation completes
    setTimeout(() => setIsFiltering(false), 600);
  };

  // Handle show more/less applications
  const handleToggleApplications = () => {
    setIsFiltering(true);
    
    if (showAllApplications) {
      // Show less - display only first 6
      const initialApps = allApplications.slice(0, INITIAL_APPLICATIONS_COUNT);
      setGridItems(initialApps);
      setShowAllApplications(false);
    } else {
      // Show all applications
      setGridItems(allApplications);
      setShowAllApplications(true);
    }
    
    // No scrolling - keep user at current position
    setTimeout(() => {
      setIsFiltering(false);
    }, 300);
  };

  // Animation variants for filter buttons with added emphasis for active state
  const buttonVariants: Variants = {
    initial: { scale: 1 },
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.98, transition: { duration: 0.2 } },
    active: { scale: 1.02, transition: { duration: 0.2 } }
  };

  // Fetch data on component mount and set initial grid items
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedIndustries = await getIndustriesWithApplications();
        setIndustries(fetchedIndustries);

        // If URL has ?industry=<slug> and it exists, initialize to applications view directly
        const param = searchParams?.get('industry');
        const selected = param ? fetchedIndustries.find(ind => ind.slug === param) : undefined;
        if (selected && selected.applications) {
          setActiveIndustry(selected.slug);
          setAllApplications(selected.applications);
          setGridItems(selected.applications.slice(0, INITIAL_APPLICATIONS_COUNT));
          setDisplayMode('applications');
          setShowAllApplications(false);
          // Smooth scroll to the grid when landing from homepage with a selected industry
          setTimeout(() => {
            const targetEl = selectorRef.current || gridRef.current;
            if (targetEl && !initialScrollDoneRef.current) {
              initialScrollDoneRef.current = true;
              smoothScrollTo(targetEl, 80);
            }
          }, 50);
        } else {
          // Default: show all industries
          setGridItems(fetchedIndustries);
          setDisplayMode('industries');
        }
      } catch (error) {
        console.error("Failed to fetch industries:", error);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize from URL query param (?industry=<slug>) once industries are loaded
  useEffect(() => {
    if (!industries || industries.length === 0) return;
    const param = searchParams?.get('industry');
    if (!param) return;

    // Only trigger if it's a valid industry slug
    const exists = industries.some(ind => ind.slug === param);
    if (exists) {
      handleIndustryChange(param);
      // If we haven't scrolled yet (e.g., client-side nav), scroll to the selector once
      setTimeout(() => {
        const targetEl = selectorRef.current || gridRef.current;
        if (targetEl && !initialScrollDoneRef.current) {
          initialScrollDoneRef.current = true;
          smoothScrollTo(targetEl, 80);
        }
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industries, searchParams]);

  return (
    <div className="flex flex-col">
      {/* Hero Section with selected banner - reduced height and whitespace */}
      <IndustryHero 
        title="Specialty Packaging Solutions for Every Industry"
        description="Discover how our BPA-free bottles and food-safe PET packaging serve diverse industries with quality, safety, and innovation at the forefront."
        imageUrl="/images/pages/industries/hero/hero-banner.jpg"
        primaryCta="Get Custom Quote"
        primaryCtaLink="/contact"
        secondaryCta="Browse Products"
        secondaryCtaLink="/products"
      />

      {/* Industries Grid Section with enhanced styling and reduced padding */}
      <section className="py-14 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
        {/* Background elements - less whitespace */}
        <div className="absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-[300px] bg-gradient-to-b from-gray-100/50 to-transparent dark:from-gray-800/50 pointer-events-none transform-gpu"></div>
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-gradient-to-tl from-primary-100/20 to-primary-200/10 dark:from-primary-800/20 dark:to-primary-900/10 rounded-full blur-3xl transform-gpu"></div>
          <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.02] dark:opacity-[0.03] mix-blend-overlay pointer-events-none transform-gpu"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl mx-auto text-center mb-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: { 
                opacity: 1,
                transition: {
                  when: "beforeChildren",
                  staggerChildren: 0.1,
                  duration: 0.4
                }
              }
            }}
          >
            <motion.div 
              className="mb-4"
              variants={sectionTitleVariants}
            >
              <Badge
                variant="premium"
                size="lg"
                radius="full"
                className="mb-4"
              >
                Our Expertise
              </Badge>
            </motion.div>
            {/* Enhanced industry selector with increased contrast */}
            <motion.div 
              ref={selectorRef}
              id="industries-selector"
              className="flex flex-wrap justify-center gap-3 mb-10"
              variants={{
                hidden: { opacity: 0 },
                visible: { 
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.05,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              {industriesList.map((industryItem) => {
                const isActive = activeIndustry === industryItem.id;
                return (
                  <motion.div key={industryItem.id} variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}>
                    <Button
                      variant={isActive ? 'primary' : 'secondary'}
                      size="md"
                      radius="full"
                      className={cn(
                        'flex items-center gap-2 font-medium px-6 py-2 whitespace-nowrap transition-all duration-200 shadow-md group',
                        isActive
                          ? 'bg-primary-600 text-white' // Active button style (no hover effect)
                          : 'text-slate-700 dark:text-slate-300 border-2 border-rose-200 dark:border-rose-800/40 hover:border-2 hover:border-rose-400 hover:shadow-sm hover:bg-rose-50 dark:hover:bg-rose-900/20' // Style with bolder border
                      )}
                      aria-pressed={isActive}
                      aria-selected={isActive}
                      role="tab"
                      tabIndex={0}
                      onClick={() => handleIndustryChange(industryItem.id)}
                      disabled={isFiltering}
                      data-id={industryItem.id}
                    >
                      <span className={cn(
                        "transition-colors duration-300",
                        isActive ? "text-white" : "text-primary-500 dark:text-primary-400 group-hover:text-rose-500 dark:group-hover:text-rose-500"
                      )}>
                        {industryItem.icon}
                      </span>
                      <span className={cn(
                        "transition-colors duration-300",
                        isActive ? "" : "group-hover:text-rose-500 dark:group-hover:text-rose-500"
                      )}>{industryItem.name}</span>
                    </Button>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>

          <motion.div 
            ref={gridRef}
            id="industries-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ 
              opacity: isFiltering ? 0.8 : 1, 
              y: 0,
              transition: { duration: 0.3 }
            }}
            className="relative rounded-2xl bg-white dark:bg-gray-800/50 p-0 sm:p-4 md:p-6 lg:p-8 shadow-sm mb-8"
          >
            {/* Subtle accent pattern for light mode only */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-primary-50 dark:bg-transparent opacity-50 rounded-full blur-3xl -z-10"></div>
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-gray-50 dark:bg-gray-800/30 opacity-70 rounded-full blur-3xl -z-10"></div>
            
            {/* Industry Grid with enhanced data and reduced spacing */}
            <IndustryGrid 
              items={gridItems}
              displayMode={displayMode}
              title="Expert Packaging, Custom-Fit for Your Sector"
              description="We equip businesses across diverse sectors—from wholesale and B2B to food, beverage, household goods, and startups—with packaging solutions expertly tailored to your industry's unique demands, ensuring product integrity and brand elevation."
            />
            
            {/* Dynamic button for industries/applications */}
            <div className="flex justify-center mt-8 mb-8">
              {displayMode === 'industries' ? (
                <motion.div
                  className="w-full sm:w-auto"
                >
                  <Button 
                    className="w-full sm:w-auto whitespace-nowrap shadow-sm shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white cursor-not-allowed"
                    onClick={(e) => e.preventDefault()}
                  >
                    View All Industries
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
              ) : (
                allApplications.length > INITIAL_APPLICATIONS_COUNT && (
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    className="w-full sm:w-auto"
                  >
                    <Button 
                      onClick={handleToggleApplications}
                      disabled={isFiltering}
                      className={cn(
                        "w-full sm:w-auto whitespace-nowrap shadow-sm transition-all duration-300",
                        showAllApplications 
                          ? "shadow-gray-500/20 bg-gray-600 hover:bg-gray-700 text-white"
                          : "shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white"
                      )}
                    >
                      {showAllApplications ? (
                        <>
                          Show Less Applications
                          <motion.div
                            animate={{ rotate: showAllApplications ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="ml-2"
                          >
                            <ArrowRight className="h-4 w-4 rotate-90" />
                          </motion.div>
                        </>
                      ) : (
                        <>
                          View All Applications ({allApplications.length - INITIAL_APPLICATIONS_COUNT} more)
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section with reduced spacing */}
      <IndustriesCTA />

      {/* Stats Section with reduced spacing */}
      <IndustriesStats />

      {/* Testimonials Section with reduced spacing */}
      <IndustriesTestimonials />
      
      {/* Final CTA section with reduced spacing */}
      <IndustriesContactCTA />
    </div>
  );
}

// Add SEO metadata for the Industries page
export const metadata = {
  title: 'Industries We Serve | Premium Packaging Solutions | Pavisha',
  description: 'Discover how Pavisha serves diverse industries with premium BPA-free bottles and food-safe PET packaging solutions. Tailored for wholesalers, education, government, beverage, and more.',
  keywords: 'packaging solutions, industries, wholesalers, educational packaging, beverage containers, food-safe packaging, PET jars, BPA-free bottles',
  openGraph: {
    title: 'Industries We Serve | Premium Packaging Solutions | Pavisha',
    description: 'Discover how Pavisha serves diverse industries with premium BPA-free bottles and food-safe PET packaging solutions.',
    images: [
      {
        url: '/og-images/industries.jpg',
        width: 1200,
        height: 630,
        alt: 'Pavisha Industries We Serve',
      },
    ],
  },
}; 